import { useRef, useState } from 'react'
import { CardContainer, VerticalContainer, VerticalMainContainer } from '@shared/designSystem/Containers'
import { H1Title, H2Title } from '@shared/designSystem/Titles'
import { NoticeBanner, NoticeFullBody, NoticeLevel } from '@shared/designSystem/NoticeBanner'
import { NoticeBar, NoticeCountPill, NoticePanel, NoticeSummaryRow } from '@shared/designSystem/NoticeBar'
import { Modal } from '@shared/designSystem/messages/Modal'
import { NormalButton } from '@shared/designSystem/Button'
import designSystemStyles from '@shared/styles/designSystemStyles'

const SAMPLES: Record<NoticeLevel, { title: string, body: string, cta?: { label: string, href: string } }> = {
    critical: {
        title: 'Las fichadas no se están registrando',
        body: 'Desde las 09:10 la app no registra entradas ni salidas. **No pidas a tu equipo que vuelva a fichar**: recuperaremos los registros cuando se restablezca el servicio.',
        cta: { label: 'Ver estado', href: 'https://status.qontact.com' },
    },
    warning: {
        title: 'Mantenimiento programado el domingo 28/09',
        body: 'De 02:00 a 04:00 (hora de Buenos Aires) el panel y los reportes no estarán disponibles.\n- Las fichadas se guardan en el teléfono\n- Se sincronizan al terminar',
    },
    communication: {
        title: 'Nuevo: exportá novedades a tu liquidación',
        body: 'Ahora podés exportar las novedades del mes directamente al formato de tu sistema de sueldos desde *Novedades → Exportar*.',
        cta: { label: 'Probarlo', href: '/novedades' },
    },
}

const labels = (level: NoticeLevel, title: string) => ({
    level: { critical: 'Aviso crítico', warning: 'Advertencia', communication: 'Comunicación' }[level],
    more: 'Ver más',
    less: 'Ver menos',
    dismiss: `Descartar aviso: ${title}`,
})

const LEVELS: NoticeLevel[] = ['critical', 'warning', 'communication']

const NoticeBannerShowcaseScreen = () => {
    const [panelOpen, setPanelOpen] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const pillRef = useRef<HTMLButtonElement>(null)

    const banner = (level: NoticeLevel, variant: 'bar' | 'card', extra: Partial<Parameters<typeof NoticeBanner>[0]> = {}) => (
        <NoticeBanner
            key={`${level}-${variant}`}
            level={level}
            variant={variant}
            title={SAMPLES[level].title}
            body={SAMPLES[level].body}
            cta={SAMPLES[level].cta}
            labels={labels(level, SAMPLES[level].title)}
            onDismiss={level === 'critical' ? undefined : () => undefined}
            {...extra}
        />
    )

    return (
        <VerticalMainContainer>
            <VerticalContainer gap={designSystemStyles.gapSm} alignment="stretch" width="100%">
                <H1Title text="NoticeBanner Showcase" />

                <H2Title text="Barra (PlatformNoticesBar) con +N avisos" />
                <CardContainer>
                    <NoticeBar
                        ariaLabel="Avisos de la plataforma"
                        hasContent={true}
                        panel={panelOpen ? (
                            <NoticePanel title="Avisos de la plataforma (3)" closeLabel="Cerrar avisos" onClose={() => setPanelOpen(false)} returnFocusTo={pillRef}>
                                {LEVELS.map((level) => banner(level, 'card', { collapsible: false }))}
                            </NoticePanel>
                        ) : undefined}
                    >
                        {banner('critical', 'bar', { meta: ['Para tu organización'] })}
                        {banner('warning', 'bar', {
                            meta: ['Actualizado'],
                            trailing: <NoticeCountPill ref={pillRef} label="+1 aviso" expanded={panelOpen} onClick={() => setPanelOpen((open) => !open)} />,
                        })}
                    </NoticeBar>
                </CardContainer>

                <H2Title text="Compacta (panel 480–760px) y resumen (< 480px)" />
                <CardContainer>
                    {banner('warning', 'bar', { density: 'compact' })}
                    <NoticeSummaryRow level="critical" levelLabel="Aviso crítico" title={SAMPLES.critical.title} countLabel="+3" expanded={false} onClick={() => undefined} />
                </CardContainer>

                <H2Title text="Tarjetas (uso dentro de páginas)" />
                <VerticalContainer gap={designSystemStyles.noticeControlPadding} alignment="stretch" width="100%">
                    {LEVELS.map((level) => banner(level, 'card'))}
                </VerticalContainer>

                <H2Title text="Modal de confirmación (crítico)" />
                <NormalButton text="Abrir modal" onClick={() => setModalOpen(true)} />
                <Modal
                    isOpen={modalOpen}
                    onClose={() => undefined}
                    dismissible={false}
                    title={SAMPLES.critical.title}
                    subtitle="Aviso crítico · Para tu organización · 25/09/2026 09:42"
                    width={designSystemStyles.noticeAckModalWidth}
                >
                    <VerticalContainer gap={designSystemStyles.gapMd} alignment="stretch" width="100%">
                        <NoticeFullBody body={SAMPLES.critical.body} cta={SAMPLES.critical.cta} />
                        <NormalButton text="Entendido" onClick={() => setModalOpen(false)} />
                    </VerticalContainer>
                </Modal>
            </VerticalContainer>
        </VerticalMainContainer>
    )
}

export default NoticeBannerShowcaseScreen
